// @ts-nocheck
//
// Session liveness store — wraps the GraphQL `sessionState` query so the SPA
// can detect a dead OR silently-recreated PHP session before rendering stale
// UI. Two failure modes this catches:
//
//   1. Tab open overnight → PHP session TTL elapses → redis key vanishes,
//      `sid` cookie still on the request. Backend reports `alive:false`,
//      we show the expiry overlay.
//   2. Same as 1, but the user (or any other request) hits PHP again
//      AFTER the TTL expired. PHP mints a FRESH empty session under the
//      same/new sid. Backend now reports `alive:true` but `hasBasket:false`
//      and `userId:null`. Pure `alive` would say "fine" — wrong. We compare
//      against the local `expectedAlive` snapshot (was-logged-in / had-items
//      at any point this session) and flag expiry on mismatch.
//
// `expectedAlive` is sticky on purpose: once we've seen the user logged in
// or with basket items, any subsequent flip to empty-and-anonymous is an
// expiry, not a normal anonymous-empty state. Reset only happens via
// `resetSession()` after the user is redirected to /index.php?cl=basket
// where a fresh session is legitimately empty.

import { query } from './graphqlClient.js';

const SESSION_STATE_QUERY = `query SessionState {
    sessionState { alive hasBasket userId }
}`;

// `alive` is tri-state for backwards-compat with existing $effects:
//   true  → backend confirmed the session is good (semantic, see below).
//   false → backend says expired (raw alive=false OR recreated-empty).
//   null  → never probed yet OR the probe itself failed. Treat as live.
//
// "Backend confirms good" means:
//   raw.alive === true AND NOT (expectedAlive && empty-anonymous)
//
// `expectedAlive` flips to true the first time we observe ANY of:
//   - sessionState.userId !== null  (PHP session has a logged-in user)
//   - sessionState.hasBasket === true (PHP basket has items)
// Once flipped, only `resetSession()` clears it (i.e. only after we
// deliberately leave checkout via the expiry-overlay redirect).
const store = $state({
    alive: null,
    /** sticky: true once the session has held real state (user or basket). */
    expectedAlive: false,
    /** Last raw probe result — kept for debug + the F32 test. */
    lastState: null,
    checking: false,
    /** ms timestamp of the last successful probe. */
    lastCheck: 0,
});

/**
 * Probe the backend. Idempotent — concurrent callers piggy-back on the
 * in-flight request via the `checking` guard. Only flips `alive` on a
 * definitive server answer; transport errors keep the previous state so a
 * flaky network doesn't trigger a false "session expired" banner.
 *
 * Decision matrix for `alive`:
 *
 *   server.alive   | hasBasket | userId  | expectedAlive | → store.alive
 *   ---------------|-----------|---------|---------------|--------------
 *   false          |  *        | *       | *             | false
 *   true           |  *        | *       | false         | true  (anon-empty is fine)
 *   true           |  true     | non-nul | true          | true
 *   true           |  true     | null    | true          | true  (still has cart → not lost)
 *   true           |  false    | non-nul | true          | true  (logged in, empty cart → user just cleared it)
 *   true           |  false    | null    | true          | false (recreated empty after user/items lost!)
 */
async function checkSession() {
    if (store.checking) return store.alive;
    store.checking = true;
    try {
        const data = await query(SESSION_STATE_QUERY);
        const s = data?.sessionState;
        if (s && typeof s.alive === 'boolean') {
            store.lastState = s;
            store.lastCheck = Date.now();
            // Update the sticky expectedAlive marker BEFORE the verdict:
            // if the server tells us we're logged in or have a basket
            // RIGHT NOW, that's the new high-water mark.
            if (s.userId || s.hasBasket) {
                store.expectedAlive = true;
            }
            store.alive = computeAlive(s, store.expectedAlive);
        }
    } catch (_) {
        // Network / transport error — leave state alone. The next call
        // (next step transition / tab focus) will retry.
    } finally {
        store.checking = false;
    }
    return store.alive;
}

function computeAlive(state, expectedAlive) {
    if (!state.alive) return false;
    if (!expectedAlive) return true; // anonymous + no basket is normal
    // expectedAlive=true: we previously had user or items. If BOTH are
    // gone now, the session was silently recreated — treat as expired.
    if (!state.hasBasket && !state.userId) return false;
    return true;
}

/**
 * Hint to the store that the client believes the session should be alive.
 * Called by basketStore when it observes items locally — covers the
 * race window before the first sessionState probe lands (e.g. user adds
 * an item, then immediately tab-out / tab-in fires checkSession and we
 * need expectedAlive to already be true).
 */
function markExpectedAlive() {
    store.expectedAlive = true;
}

/**
 * Reset back to the unknown state. Used after a redirect to the basket
 * step where a fresh session may have been issued by PHP — the next probe
 * should not get short-circuited by a stale `alive === false` or by the
 * sticky expectedAlive flag.
 */
function resetSession() {
    store.alive = null;
    store.expectedAlive = false;
    store.lastState = null;
    store.lastCheck = 0;
}

export {
    store as sessionStore,
    checkSession,
    markExpectedAlive,
    resetSession,
};
